import { createSlice, createAction } from '@reduxjs/toolkit';
import { DEFAULT_WALLET_STATE, LOW_BALANCE_THRESHOLD } from '../utils/wallet/walletConstants';
import { saveWalletToStorage, mapLedgerToTransactions } from '../utils/wallet/walletUtils';

export const hydrateWallet = createAction('HYDRATE_WALLET');
export const fetchWalletBalance = createAction('FETCH_WALLET_BALANCE');
export const fetchWalletLedger = createAction('FETCH_WALLET_LEDGER');
export const estimateWalletCost = createAction('ESTIMATE_WALLET_COST');

const getInitialState = () => ({
  balance: DEFAULT_WALLET_STATE.balance,
  baselinePool: DEFAULT_WALLET_STATE.baselinePool,
  warningThreshold: LOW_BALANCE_THRESHOLD,
  lastRechargeText: DEFAULT_WALLET_STATE.lastRechargeText,
  transactions: DEFAULT_WALLET_STATE.transactions,
  alertPreferences: { ...DEFAULT_WALLET_STATE.alertPreferences },
  lastRechargeDetails: null,
  showRechargeSuccessModal: false,
  hydrated: false,
  balanceLoading: false,
  balanceError: null,
  balanceFetched: false,
  ledgerLoading: false,
  ledgerError: null,
  ledgerFetched: false,
  costEstimate: null,
  costEstimateLoading: false,
  costEstimateError: null,
});

const initialState = getInitialState();

const walletSlice = createSlice({
  name: 'wallet',
  initialState,
  reducers: {
    hydrateWalletSuccess: (state, action) => {
      const saved = action.payload;
      if (saved?.alertPreferences) {
        state.alertPreferences = saved.alertPreferences;
      }
      state.hydrated = true;
    },
    fetchWalletBalanceRequest: (state) => {
      state.balanceLoading = true;
      state.balanceError = null;
    },
    fetchWalletBalanceSuccess: (state, action) => {
      const { balance, warningThreshold } = action.payload || {};
      state.balance = balance ?? 0;
      if (warningThreshold != null) {
        state.warningThreshold = warningThreshold;
      }
      state.balanceLoading = false;
      state.balanceError = null;
      state.balanceFetched = true;
    },
    fetchWalletBalanceFailure: (state, action) => {
      state.balanceLoading = false;
      state.balanceError = action.payload;
      state.balanceFetched = true;
    },
    fetchWalletLedgerRequest: (state) => {
      state.ledgerLoading = true;
      state.ledgerError = null;
    },
    fetchWalletLedgerSuccess: (state, action) => {
      state.transactions = mapLedgerToTransactions(action.payload?.history);
      state.ledgerLoading = false;
      state.ledgerError = null;
      state.ledgerFetched = true;
    },
    fetchWalletLedgerFailure: (state, action) => {
      state.ledgerLoading = false;
      state.ledgerError = action.payload;
      state.ledgerFetched = true;
    },
    estimateWalletCostRequest: (state) => {
      state.costEstimateLoading = true;
      state.costEstimateError = null;
    },
    // Response: { estimatedCost, gst, totalCostWithGst, currentBalance, rechargeNeeded, rechargeCostWithGst }.
    estimateWalletCostSuccess: (state, action) => {
      state.costEstimate = action.payload;
      state.costEstimateLoading = false;
      state.costEstimateError = null;
    },
    estimateWalletCostFailure: (state, action) => {
      state.costEstimateLoading = false;
      state.costEstimateError = action.payload;
    },
    clearCostEstimate: (state) => {
      state.costEstimate = null;
      state.costEstimateLoading = false;
      state.costEstimateError = null;
    },
    setWalletRechargeSuccess: (state, action) => {
      const { baseAmount, gstAmount, total, paymentId } = action.payload;
      state.lastRechargeDetails = {
        invoiceId: paymentId,
        baseAmount,
        gstAmount,
        total,
        date: new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }),
      };
      state.showRechargeSuccessModal = true;
    },
    closeWalletRechargeSuccessModal: (state) => {
      state.showRechargeSuccessModal = false;
    },
    updateAlertPreferences: (state, action) => {
      state.alertPreferences = { ...state.alertPreferences, ...action.payload };
    },
    persistWalletState: (state, action) => {
      saveWalletToStorage(state, action.payload?.companyId);
    },
  },
});

export const {
  hydrateWalletSuccess,
  fetchWalletBalanceRequest,
  fetchWalletBalanceSuccess,
  fetchWalletBalanceFailure,
  fetchWalletLedgerRequest,
  fetchWalletLedgerSuccess,
  fetchWalletLedgerFailure,
  estimateWalletCostRequest,
  estimateWalletCostSuccess,
  estimateWalletCostFailure,
  clearCostEstimate,
  setWalletRechargeSuccess,
  closeWalletRechargeSuccessModal,
  updateAlertPreferences,
  persistWalletState,
} = walletSlice.actions;

export default walletSlice.reducer;
